import { rpcSlice } from '../../../store/api/rpcSlice';

// Shared types for artifacts
export interface ArtifactListItem {
  id: string;
  owner_person_id: string;
  subject_id?: string;
  profile_slug: string;
  title?: string;
  sources?: Array<{ type: string; value: string; label?: string | null }>;
  updated_at: string;
  revision: number;
  transcript_sessions?: Array<{
    transcript_id: string;
    session_start_at?: string | null;
    /** Moments sessions.id (the id session routes accept). */
    session_id?: string | null;
    /** Rails sessions.id, when the Moments session has one. */
    legacy_session_id?: string | null;
    session_type?: string | null;
  }>;
}

export interface ArtifactListResponse {
  items: ArtifactListItem[];
  total_count: number;
  has_more: boolean;
  next_offset?: number;
}

export interface ArtifactListParams {
  owner_person_id: string;
  profile_slug?: string;
  limit?: number;
  offset?: number;
}

export interface ArtifactGetResponse {
  id: string;
  owner_person_id: string;
  owner_display_name?: string | null;
  subject_id?: string;
  profile_slug: string;
  title: string;
  sources: Array<{ type: string; value: string; label?: string | null }>;
  markdown: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  revision: number;
  transcript_sessions?: Array<{
    transcript_id: string;
    session_start_at?: string | null;
    /** Moments sessions.id (the id session routes accept). */
    session_id?: string | null;
    /** Rails sessions.id, when the Moments session has one. */
    legacy_session_id?: string | null;
    session_type?: string | null;
  }>;
  // Shared status tracking
  shared_at?: string | null;
  shared_with_email?: string | null;
  shared_revision?: number | null;
}

export interface ArtifactGetParams {
  id: string;
  revision?: number;
  member_id?: string;
  transcript_id?: string;
}

export interface ArtifactUpdateRequest {
  id: string;
  markdown: string;
  revision: number;
  actor_id: string;
  edit_session_id?: string;
}

export interface ArtifactUpdateResponse {
  revision: number;
}

export interface ArtifactVersionItem {
  revision: number;
  updated_at: string;
  updated_by: string;
}

export interface ArtifactVersionsResponse {
  artifact_id: string;
  versions: ArtifactVersionItem[];
  total_count: number;
}

export interface ArtifactVersionsParams {
  id: string;
  limit?: number;
  member_id?: string;
  transcript_id?: string;
}

export interface ArtifactListBySourcesParams {
  owner_person_id: string;
  profile_slug: string;
  source_ids: string[]; // Array of transcript IDs or drive_file_ids
  limit?: number;
}

export interface ArtifactShareRequest {
  id: string;
  recipient_email: string;
}

export interface ArtifactShareResponse {
  success: boolean;
  shared_at: string;
  message?: string;
}

export const artifactApi = rpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * List artifacts with optional pagination and filtering
     */
    listArtifacts: builder.query<ArtifactListResponse, ArtifactListParams>({
      query: (params) => ({
        url: '/artifact.list',
        method: 'GET',
        params: {
          owner_person_id: params.owner_person_id,
          ...(params.profile_slug ? { profile_slug: params.profile_slug } : {}),
          limit: params.limit?.toString() || '50',
          offset: params.offset?.toString() || '0',
        },
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((item) => ({ type: 'Artifacts' as const, id: item.id })),
              { type: 'Artifacts' as const, id: 'ARTIFACT_LIST' },
            ]
          : [{ type: 'Artifacts' as const, id: 'ARTIFACT_LIST' }],
    }),

    /**
     * Get a single artifact by ID
     */
    getArtifact: builder.query<ArtifactGetResponse, ArtifactGetParams>({
      query: ({ id, revision, member_id, transcript_id }) => ({
        url: '/artifact.get',
        method: 'GET',
        params: {
          id,
          ...(revision ? { revision: revision.toString() } : {}),
          ...(member_id ? { member_id } : {}),
          ...(transcript_id ? { transcript_id } : {}),
        },
      }),
      providesTags: (result, _err, arg) =>
        result
          ? [
              { type: 'Artifacts' as const, id: result.id },
              { type: 'Artifacts' as const, id: `${result.id}-${arg.revision ?? 'latest'}` },
            ]
          : [{ type: 'Artifacts' as const, id: arg.id }],
    }),

    /**
     * List versions for an artifact
     */
    listArtifactVersions: builder.query<ArtifactVersionsResponse, ArtifactVersionsParams>({
      query: ({ id, limit, member_id, transcript_id }) => ({
        url: '/artifact.list-versions',
        method: 'GET',
        params: {
          id,
          limit: (limit ?? 15).toString(),
          ...(member_id ? { member_id } : {}),
          ...(transcript_id ? { transcript_id } : {}),
        },
      }),
      providesTags: (_result, _err, arg) => [{ type: 'ArtifactVersions' as const, id: arg.id }],
    }),

    /**
     * List artifacts filtered by source IDs (optimized for finding artifacts by transcript/drive_file IDs)
     */
    listArtifactsBySources: builder.query<ArtifactListResponse, ArtifactListBySourcesParams>({
      query: ({ owner_person_id, profile_slug, source_ids, limit }) => ({
        url: '/artifact.list-by-sources',
        method: 'GET',
        params: {
          owner_person_id,
          profile_slug,
          source_ids: source_ids.join(','), // Comma-separated for query param
          limit: limit?.toString() || '1000',
        },
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((item) => ({ type: 'Artifacts' as const, id: item.id })),
              { type: 'Artifacts' as const, id: 'ARTIFACT_LIST' },
            ]
          : [{ type: 'Artifacts' as const, id: 'ARTIFACT_LIST' }],
    }),

    /**
     * Update an artifact
     */
    updateArtifact: builder.mutation<ArtifactUpdateResponse, ArtifactUpdateRequest>({
      query: (body) => ({
        url: '/artifact.update',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _err, arg) => [
        { type: 'Artifacts' as const, id: arg.id },
        { type: 'Artifacts' as const, id: 'ARTIFACT_LIST' },
        { type: 'ArtifactVersions' as const, id: arg.id },
      ],
    }),

    /**
     * Share an artifact with a recipient
     */
    shareArtifact: builder.mutation<ArtifactShareResponse, ArtifactShareRequest>({
      query: (body) => ({
        url: '/artifact.share',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _err, arg) => [{ type: 'Artifacts' as const, id: arg.id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListArtifactsQuery,
  useLazyListArtifactsQuery,
  useGetArtifactQuery,
  useLazyGetArtifactQuery,
  useListArtifactVersionsQuery,
  useLazyListArtifactVersionsQuery,
  useListArtifactsBySourcesQuery,
  useLazyListArtifactsBySourcesQuery,
  useUpdateArtifactMutation,
  useShareArtifactMutation,
} = artifactApi;
